'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'

import { cn } from '@/utils/shadcn/utils'

interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  onTabChange?: (value: string) => void
  value?: string
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ onTabChange, value, ...props }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    onValueChange={onTabChange}
    value={value}
    {...props}
  />
))
Tabs.displayName = TabsPrimitive.Root.displayName

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex',
      'p-0',
      'm-0',
      'items-center',
      'justify-center',
      'text-muted-foreground',
      'border-b',
      'border-primary-border',
      'relative', // Added relative positioning
      className,
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'h-[40px]',
      'inline-flex',
      'items-center',
      'justify-center',
      'whitespace-nowrap',
      'mx-3',
      'first:ml-0',
      'last:mr-0',
      'py-1',
      'text-sm',
      'font-medium',
      'ring-offset-background',
      'transition-all',
      'focus-visible:outline-hidden',
      'focus-visible:ring-2',
      'focus-visible:ring-ring',
      'focus-visible:ring-offset-2',
      'disabled:pointer-events-none',
      'disabled:opacity-50',
      'data-[state=active]:text-foreground',
      'border-b-2',
      'border-transparent',
      'data-[state=active]:border-moment-teal-800',
      'hover:data-[state=active]:[&>div>p]:bg-moment-teal-300',
      'dark:hover:data-[state=active]:[&>div>p]:bg-moment-teal-900',
      'data-[state=inactive]:[&>div>p]:text-moment-blue-grey-800',
      'dark:data-[state=inactive]:[&>div>p]:text-moment-grey-400',
      'hover:data-[state=inactive]:[&>div>p]:bg-moment-blue-grey-200',
      'dark:hover:data-[state=inactive]:[&>div>p]:bg-moment-grey-800',
      'hover:data-[state=inactive]:[&>div>p]:text-moment-blue-grey-800',
      'dark:hover:data-[state=inactive]:[&>div>p]:text-moment-white-100',
      'hover:[&>div]:bg-transparent',
      '[&>div>p]:px-[4px]',
      '[&>div>p]:py-[2px]',
      '[&>div>p]:rounded-[6px]',
      '[&>div>p]:my-[10px]',
      'relative', // Added relative positioning
      'bottom-[-1px]', // Move the trigger down by 2px
      'hover:cursor-pointer',
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2',
      'ring-offset-background',
      'focus-visible:outline-hidden',
      'focus-visible:ring-2',
      'focus-visible:ring-ring',
      'focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger }
